import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { workspaceSharingService } from "@/services/workspaceSharingService";
import { Users, UserMinus } from "lucide-react";
import type { WorkspaceShare } from "@shared/schema";

interface WorkspaceMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  workspaceName: string;
}

export function WorkspaceMembersDialog({ open, onOpenChange, workspaceId, workspaceName }: WorkspaceMembersDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [memberToRemove, setMemberToRemove] = useState<{ userId: string; label: string } | null>(null);

  const { data: existingShare } = useQuery<WorkspaceShare | null>({
    queryKey: ["/api/workspaces", workspaceId, "share"],
    queryFn: async () => {
      return await workspaceSharingService.getShareLink(workspaceId);
    },
    enabled: open && !!workspaceId,
  });

  const shareLinkActive = !!existingShare;

  const { data: workspaceMembers = [], isLoading: isLoadingMembers } = useQuery({
    queryKey: ["workspace-members", workspaceId],
    queryFn: () => workspaceSharingService.getWorkspaceMembers(workspaceId),
    enabled: open && !!workspaceId,
  });

  const { data: displayLabels = {} } = useQuery({
    queryKey: ["workspace-member-display-labels", workspaceId],
    queryFn: () => workspaceSharingService.getWorkspaceMemberDisplayLabels(workspaceId),
    enabled: open && !!workspaceId,
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberUserId: string) => {
      await workspaceSharingService.removeMember(workspaceId, memberUserId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace-members", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["workspace-member-display-labels", workspaceId] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "share"] });
      setMemberToRemove(null);
      toast({ title: "Member removed", description: "They no longer have access to this workspace." });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not remove member",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const updateMemberRoleMutation = useMutation({
    mutationFn: async ({ memberUserId, role }: { memberUserId: string; role: "editor" | "viewer" }) => {
      await workspaceSharingService.updateMemberWorkspaceShareRole(workspaceId, memberUserId, role);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace-members", workspaceId] });
      toast({ title: "Role updated" });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not update role",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const memberDisplayLine = (userId: string) => {
    if (user?.id === userId) return "You";
    const label = displayLabels[userId];
    if (label) return label;
    return `Member (${userId.slice(0, 8)}…)`;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Workspace members
            </DialogTitle>
            <DialogDescription>
              People who can access &quot;{workspaceName}&quot;. Editors can change workspace content; viewers are read-only.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <Label className="text-foreground">People with access</Label>
            {isLoadingMembers ? (
              <p className="text-sm text-muted-foreground py-2">Loading members…</p>
            ) : workspaceMembers.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No members yet.</p>
            ) : (
              <ul className="rounded-md border divide-y max-h-[min(24rem,50vh)] overflow-y-auto">
                {workspaceMembers.map((m) => {
                  const isOwnerRow = m.workspaceRole === "owner";
                  return (
                    <li key={m.userId} className="flex items-center gap-2 px-3 py-2.5 text-sm">
                      <span className="truncate flex-1 min-w-0" title={m.userId}>
                        {memberDisplayLine(m.userId)}
                      </span>
                      {isOwnerRow ? (
                        <Badge variant="outline" className="shrink-0 text-[10px] capitalize">
                          Owner
                        </Badge>
                      ) : (
                        <>
                          <Select
                            value={m.workspaceRole === "viewer" ? "viewer" : "editor"}
                            onValueChange={(v) => {
                              const role = v as "editor" | "viewer";
                              updateMemberRoleMutation.mutate({ memberUserId: m.userId, role });
                            }}
                            disabled={!shareLinkActive || updateMemberRoleMutation.isPending}
                          >
                            <SelectTrigger className="h-8 w-[110px] shrink-0 text-xs" aria-label="Member role">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="viewer">Viewer</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                            onClick={() => setMemberToRemove({ userId: m.userId, label: memberDisplayLine(m.userId) })}
                            disabled={removeMemberMutation.isPending}
                            aria-label="Remove member"
                            data-testid="button-workspace-remove-member"
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {!shareLinkActive && workspaceMembers.some((m) => m.workspaceRole !== "owner") && (
              <p className="text-xs text-muted-foreground">
                Activate a share link (Share → Generate share link) to change invitee roles. You can still remove members below.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!memberToRemove} onOpenChange={(o) => !o && setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              {memberToRemove && (
                <>
                  Remove <strong>{memberToRemove.label}</strong> from &quot;{workspaceName}&quot;? They will lose access to workspace spaces and shared content.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => memberToRemove && removeMemberMutation.mutate(memberToRemove.userId)}
            >
              {removeMemberMutation.isPending ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
